import { ArrowLeftOutlined, CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Input, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { MeasurementValueTypeDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EditorPanel } from "../components/EditorPanel";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { LocalizedFields } from "../components/LocalizedFields";
import { TranslationStatus } from "../components/TranslationStatus";
import { useColumnSearch } from "../components/useColumnSearch";
import {
    addMeasurementValueType,
    clearErrorMeasurementValueType,
    deleteMeasurementValueType,
    fetchMeasurementValueTypes,
    selectMeasurementValueType,
    startAddMeasurementValueType,
    startDeleteMeasurementValueType,
    updateMeasurementValueType
} from "../features/measurementValueTypesSlice";
import { localizationEnabled } from "../config/locales";
import { measurementValueTypeFieldKeys, measurementValueTypeFields } from "../model/localizedEntities";
import { allLocalizedValues } from "../utils/translations";

export const MeasurementValueTypesPage = () => {

    const dispatch = useAppDispatch()
    const { t } = useTranslation();
    const measurementValueTypes = useAppSelector(state => state.measurementValueTypes.measurementValueTypes);
    const selectedMeasurementValueType = useAppSelector(state => state.measurementValueTypes.selectedMeasurementValueType);
    const status = useAppSelector(state => state.measurementValueTypes.status);
    const error = useAppSelector(state => state.measurementValueTypes.error);
    const params = useParams();
    const navigate = useNavigate();

    const measurementTypeId = params.measurementTypeId;

    // Names the measurement these value types belong to. The parent list is in
    // the store even on a direct load of this URL, because `MeasurementTypesPage`
    // fetches before it hands over to this outlet.
    const parentMeasurementType = useAppSelector(state =>
        state.measurementTypes.measurementTypes?.find(type => type.id === measurementTypeId)
    );

    useEffect(() => {
        if (measurementTypeId !== undefined) {
            dispatch(fetchMeasurementValueTypes(measurementTypeId!));
        }
    }, []);

    const columnSearch = useColumnSearch<MeasurementValueTypeDto>({
        placeholder: t("placeholder.searchValueType"),
        getSearchValues: valueType => [
            valueType.uoM,
            ...allLocalizedValues(valueType, measurementValueTypeFieldKeys)
        ]
    });

    if (params.measurementValueTypeId !== undefined) {
        return <Outlet />
    }

    const onRow = (measurementValueType: MeasurementValueTypeDto) => ({
        onClick: () => {
            dispatch(selectMeasurementValueType(measurementValueType))
        },
    });

    const columns: ColumnsType<MeasurementValueTypeDto> = [
        {
            width: '25%',
            title: t("column.unitOfMeasurement"),
            dataIndex: 'uoM',
            key: 'uoM',
            sorter: (a, b) => a.uoM! > b.uoM! ? -1 : 1
        },
        {
            title: t("column.description"),
            dataIndex: 'description',
            key: 'description',
            ...columnSearch,
            sorter: (a, b) => a.description! > b.description! ? -1 : 1
        },
        ...(localizationEnabled ? [{
            width: 200,
            title: t("column.translations"),
            key: 'translations',
            render: (_: unknown, valueType: MeasurementValueTypeDto) => (
                <TranslationStatus entity={valueType} fields={measurementValueTypeFieldKeys} />
            )
        }] : [])
    ];

    const getRowClassName = (measurementValueType: MeasurementValueTypeDto) => {
        return selectedMeasurementValueType != null && selectedMeasurementValueType.id === measurementValueType.id ? 'bg-selected' : '';
    }

    const onSaveMeasurementValueType = () => {
        if (selectedMeasurementValueType?.description == null) return;

        if (selectedMeasurementValueType.id == null) {
            dispatch(addMeasurementValueType({ measurementTypeId: measurementTypeId!, measurementValueType: selectedMeasurementValueType }));
            return;
        }

        dispatch(updateMeasurementValueType({ measurementTypeId: measurementTypeId!, measurementValueType: selectedMeasurementValueType }));
    }

    const canSave = selectedMeasurementValueType?.description != null
        && selectedMeasurementValueType.description.trim().length > 0;

    const addForm = status === "adding" ? (
        <EditorPanel
            title={t(selectedMeasurementValueType?.id != null ? "editor.edit" : "editor.new")}
            actions={
                <>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => dispatch(clearErrorMeasurementValueType())}
                    >
                        {t("actions.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSave}
                        icon={<SaveOutlined />}
                        onClick={onSaveMeasurementValueType}
                    >
                        {t("actions.save")}
                    </Button>
                </>
            }
        >
            <Form name="basic" layout="vertical" autoComplete="off">
                {/* Units of measurement are language-neutral, so `uoM` stays outside the locale tabs. */}
                <Form.Item label={t("field.unitOfMeasure")} style={{ maxWidth: 220 }}>
                    <Input
                        value={selectedMeasurementValueType?.uoM ?? ""}
                        onChange={e => dispatch(selectMeasurementValueType({ ...selectedMeasurementValueType, uoM: e.target.value }))}
                    />
                </Form.Item>
                <LocalizedFields
                    fields={measurementValueTypeFields}
                    value={selectedMeasurementValueType ?? {}}
                    onChange={valueType => dispatch(selectMeasurementValueType(valueType))}
                />
            </Form>
        </EditorPanel>
    ) : null;

    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            {status !== "adding" ? <CrudToolbar
                hasSelection={selectedMeasurementValueType != null}
                canEdit={selectedMeasurementValueType?.id != null}
                selectionLabel={selectedMeasurementValueType?.description}
                onDelete={() => { if (selectedMeasurementValueType != null) dispatch(startDeleteMeasurementValueType()); }}
                onEdit={() => { if (selectedMeasurementValueType != null) dispatch(startAddMeasurementValueType(selectedMeasurementValueType)); }}
                onAdd={() => dispatch(startAddMeasurementValueType({} as MeasurementValueTypeDto))}
                onReload={() => { if (measurementTypeId !== undefined) dispatch(fetchMeasurementValueTypes(measurementTypeId)); }}
                onClearSelection={() => dispatch(selectMeasurementValueType(undefined))}
            /> : null}
            {addForm}
            <div className="card card--flush">
                <Table
                    rowKey={(measurementValueType: MeasurementValueTypeDto) => measurementValueType.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={measurementValueTypes}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="measurementValueType"
                deleteTarget={selectedMeasurementValueType?.description}
                onDismiss={() => dispatch(clearErrorMeasurementValueType())}
                onConfirmDelete={() => { if (selectedMeasurementValueType != null) dispatch(deleteMeasurementValueType({ measurementTypeId: measurementTypeId!, id: selectedMeasurementValueType.id! })); }}
            />
        </>

    return (
        <>
            <PageHeader
                eyebrow={parentMeasurementType?.description ?? t("page.measurementTypes.title")}
                title={t("page.measurementValueTypes.title")}
                count={measurementValueTypes?.length}
                actions={
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                        {t("actions.back")}
                    </Button>
                }
            />
            {content}
        </>
    );
}
